import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, DatePicker, InputNumber, Select, Switch, Space, Divider, Typography, Row, Col, Spin } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { getMailingMetadataOptions } from '../api/customer';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

/**
 * MailingMetadataModal Component
 * 
 * Collects mailing campaign metadata before file upload
 */
const MailingMetadataModal = ({ visible, onConfirm, onCancel, keyCodes = [] }) => {
  const [form] = Form.useForm();
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(false);

  // Load available options from backend
  useEffect(() => {
    if (visible) {
      loadOptions();
    }
  }, [visible]);

  const loadOptions = async () => {
    setLoading(true);
    try {
      const data = await getMailingMetadataOptions();
      setOptions(data);
    } catch (error) {
      console.error('Error loading options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    form.validateFields().then(values => {
      // Convert date to ISO string
      if (values.mail_date) {
        values.mail_date = values.mail_date.format('YYYY-MM-DD');
      }
      if (values.last_updated_date) {
        values.last_updated_date = values.last_updated_date.format('YYYY-MM-DD');
      }
      
      onConfirm(values);
      form.resetFields();
    }).catch(info => {
      console.log('Validation Failed:', info);
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={
        <Space>
          <InfoCircleOutlined style={{ color: '#1890ff' }} />
          <span>Mailing Campaign Information</span>
        </Space>
      }
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      width={900}
      okText="Upload File"
      cancelText="Cancel"
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Please provide information about this mailing campaign. This will be associated with {keyCodes.length > 0 ? `${keyCodes.length} unique key code(s)` : 'the key codes'} in your file.
      </Text>

      {keyCodes.length > 0 && (
        <div style={{ marginBottom: 16, padding: 12, background: '#f0f0f0', borderRadius: 4 }}>
          <Text strong>Key Codes in file: </Text>
          <Text>{keyCodes.slice(0, 5).join(', ')}{keyCodes.length > 5 ? ` and ${keyCodes.length - 5} more...` : ''}</Text>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin tip="Loading options..." />
        </div>
      ) : (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            to_consider_to_ignore: false,
            qty_mailed: 0
          }}
        >
          <Divider orientation="left">Basic Information</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="client"
                label="Client/Owner Name"
                rules={[{ required: true, message: 'Please enter client name' }]}
              >
                <Select
                  showSearch
                  placeholder="Select or type client name"
                  allowClear
                  mode="tags"
                  maxTagCount={1}
                >
                  {options.clients?.map(client => (
                    <Option key={client} value={client}>{client}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="file_code"
                label="File Code"
                rules={[{ required: true, message: 'Please enter file code' }]}
                tooltip="Identifier for this file/mailing"
              >
                <Select
                  showSearch
                  placeholder="Select or type file code"
                  allowClear
                  mode="tags"
                  maxTagCount={1}
                >
                  {options.file_codes?.map(code => (
                    <Option key={code} value={code}>{code}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="campaign_code"
                label="Campaign Code"
                tooltip="Optional campaign identifier"
              >
                <Select
                  showSearch
                  placeholder="Select or type campaign code"
                  allowClear
                  mode="tags"
                  maxTagCount={1}
                >
                  {options.campaign_codes?.map(code => (
                    <Option key={code} value={code}>{code}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="mail_date"
                label="Mail Date"
                rules={[{ required: true, message: 'Please select mail date' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Product Information</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="offer_code"
                label="Offer Code"
                rules={[{ required: true, message: 'Please enter offer code' }]}
              >
                <Select
                  showSearch
                  placeholder="Select or type offer code"
                  allowClear
                  mode="tags"
                  maxTagCount={1}
                >
                  {options.offer_codes?.map(code => (
                    <Option key={code} value={code}>{code}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="product_name"
                label="Product Name"
                rules={[{ required: true, message: 'Please enter product name' }]}
              >
                <Select
                  showSearch
                  placeholder="Select or type product name"
                  allowClear
                  mode="tags"
                  maxTagCount={1}
                >
                  {options.product_names?.map(name => (
                    <Option key={name} value={name}>{name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="product_category"
                label="Product Category"
                rules={[{ required: true, message: 'Please enter product category' }]}
              >
                <Select
                  showSearch
                  placeholder="Select or type product category"
                  allowClear
                  mode="tags"
                  maxTagCount={1}
                >
                  {options.product_categories?.map(cat => (
                    <Option key={cat} value={cat}>{cat}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="qty_mailed"
                label="Quantity Mailed"
                rules={[{ required: true, message: 'Please enter quantity mailed' }]}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={0}
                  placeholder="Number of pieces mailed"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">List Information</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="list_name_original"
                label="Original List Name"
                tooltip="Original name of the mailing list"
              >
                <Select
                  showSearch
                  placeholder="Select or type list name"
                  allowClear
                  mode="tags"
                  maxTagCount={1}
                >
                  {options.list_names_original?.map(name => (
                    <Option key={name} value={name}>{name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="list_segment_name"
                label="List Segment Name"
                tooltip="Specific segment within the list"
              >
                <Select
                  showSearch
                  placeholder="Select or type segment name"
                  allowClear
                  mode="tags"
                  maxTagCount={1}
                >
                  {options.list_segment_names?.map(name => (
                    <Option key={name} value={name}>{name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">SVG Classification</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="list_recency_svg"
                label="List Recency (SVG)"
                tooltip="Recency classification"
              >
                <Select
                  showSearch
                  placeholder="Select or type recency"
                  allowClear
                  mode="tags"
                  maxTagCount={1}
                >
                  {options.list_recency_svg?.map(rec => (
                    <Option key={rec} value={rec}>{rec}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="list_name_svg"
                label="List Name (SVG)"
                tooltip="SVG list name classification"
              >
                <Select
                  showSearch
                  placeholder="Select or type SVG list name"
                  allowClear
                  mode="tags"
                  maxTagCount={1}
                >
                  {options.list_name_svg?.map(name => (
                    <Option key={name} value={name}>{name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="list_category_svg"
                label="List Category (SVG)"
                tooltip="SVG category classification"
              >
                <Select
                  showSearch
                  placeholder="Select or type SVG category"
                  allowClear
                  mode="tags"
                  maxTagCount={1}
                >
                  {options.list_category_svg?.map(cat => (
                    <Option key={cat} value={cat}>{cat}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">Additional Settings</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="to_consider_to_ignore"
                label="Mark to Ignore"
                valuePropName="checked"
                tooltip="Check this to mark this mailing for exclusion from analysis"
              >
                <Switch />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="last_updated_date"
                label="Last Updated Date"
                tooltip="Date when parameters were last updated"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default MailingMetadataModal;
